/**
 * AppFrame. The chrome around every signed-in page.
 * Spec: `brand-redesign-docs/04-SURFACES-AND-COMPONENTS.md` (Sidebar).
 *
 * Mounted once as a layout route, so the frame survives navigation: the
 * sidebar's active pill slides between items instead of remounting, and
 * the scroll position resets per page.
 *
 *   desktop (1024+)  sidebar, always visible, beside the canvas
 *   below 1024       the app shell: a slim top bar and a bottom tab bar
 *                    with a More sheet (`12-MOBILE-APP.md`)
 *
 * The sidebar is mint in light mode and a teal-black gradient in dark mode.
 * The canvas carries the page's soft light source and a paper grain behind a
 * 1440px content column.
 */

import { Suspense, useEffect, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { CommandPaletteHost } from "@/components/command/CommandPaletteHost";
import { KeyboardShortcuts } from "@/components/command/KeyboardShortcuts";
import { HelpLauncher } from "@/components/support/HelpLauncher";
import { SupportSessionBanner } from "@/components/support/SupportSessionBanner";
import { useDeviceClass } from "@/hooks/useMediaQuery";
import "@/primitives/brand/brand.css";
import "@/primitives/surface/studio.css";
import { LoadGroup } from "@/primitives/ui/LoadGroup";

import { TopBar } from "./frame/Compact";
import { Sidebar } from "./frame/Sidebar";
import { TabBar } from "./frame/TabBar";
import "./frame/frame.css";

export function AppFrame({ children }: { children?: ReactNode }) {
  const device = useDeviceClass();
  const isDesktop = device === "desktop";
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <a href="#main" className="fr-skip">
        Skip to main content
      </a>
      {isDesktop ? (
        <aside className="fr-side" data-variant="fixed" aria-label="Sidebar">
          <Sidebar />
        </aside>
      ) : (
        <>
          <TopBar />
          <TabBar />
        </>
      )}

      <main id="main" tabIndex={-1} className="fr-main" data-shell={isDesktop ? "side" : "bar"}>
        <div className="light-source" aria-hidden />
        <div className="grain grain-soft" aria-hidden />
        <div className="fr-content">
          <Suspense fallback={<PageFallback />}>
            <LoadGroup key={pathname}>{children ?? <Outlet />}</LoadGroup>
          </Suspense>
        </div>
      </main>

      <CommandPaletteHost />
      <KeyboardShortcuts />
      <HelpLauncher />
      <SupportSessionBanner />
    </>
  );
}

/** Held while a page's code chunk loads (rare, since pages preload). Every
 * page opens with a hero panel, so the fallback is that panel in its loading
 * paint (10-SKELETONS): a quiet frame with bones for the tag, the greeting
 * and the summary line, at the real type geometry. */
function PageFallback() {
  return (
    <div className="lg" data-state="loading" aria-busy="true" aria-label="Loading">
      <header className="br-page-hero" aria-hidden>
        <div style={{ display: "flex", flexDirection: "column", gap: 9, alignItems: "flex-start" }}>
          <span className="br-tag">Loading</span>
          <h1 className="st-greeting">Opening your page</h1>
          <p className="st-body">Getting everything ready for you</p>
        </div>
      </header>
    </div>
  );
}
